import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useBusinessPlan } from '../hooks/useBusinessPlan';
import AnimatedPage from '../components/shared/AnimatedPage';
import LoadingScreen from '../components/shared/LoadingScreen';
import PublishModal from '../components/shared/PublishModal';

interface ToolCard {
  id: string;
  name: string;
  description: string;
  icon: string;
  path: string;
  active: boolean;
}

const TOOL_CARDS: ToolCard[] = [
  {
    id: 'pitch',
    name: 'Pitch Deck',
    description: 'Present your idea',
    icon: '🎤',
    path: 'pitch',
    active: true,
  },
  {
    id: 'dashboard',
    name: 'Business Dashboard',
    description: 'Track your progress',
    icon: '📊',
    path: 'dashboard',
    active: true,
  },
  {
    id: 'website',
    name: 'Website Builder',
    description: 'Create your website',
    icon: '🌐',
    path: 'website',
    active: true,
  },
  {
    id: 'marketing',
    name: 'Marketing Studio',
    description: 'Spread the word',
    icon: '📢',
    path: 'marketing',
    active: true,
  },
  {
    id: 'catalogue',
    name: 'Product Catalogue',
    description: 'Showcase products',
    icon: '📦',
    path: 'catalogue',
    active: true,
  },
];

export default function BusinessHQPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { plan, loading } = useBusinessPlan(planId);
  const [showPublish, setShowPublish] = useState(false);

  if (loading) return <LoadingScreen />;

  if (!plan) {
    return (
      <AnimatedPage>
        <div className="flex min-h-screen items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Plan not found</h1>
            <p className="mt-2 text-gray-500">Unable to load your business plan.</p>
          </div>
        </div>
      </AnimatedPage>
    );
  }

  const completedCount = plan.completedStages?.length ?? 0;
  const startDate = plan.createdAt?.toDate
    ? plan.createdAt.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown';

  return (
    <AnimatedPage>
      <div className="min-h-screen bg-gradient-to-b from-brand-50/50 to-white px-4 pb-12 pt-8">
        {/* Header */}
        <div className="mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
              Business HQ —{' '}
              <span className="text-brand-600">
                {plan.businessName || plan.explorerName}
              </span>
            </h1>
            <div className="mt-2 flex items-center justify-center gap-4 text-sm text-gray-500">
              <span>{completedCount}/6 stages completed</span>
              <span className="text-gray-300">•</span>
              <span>Started {startDate}</span>
            </div>
            <button
              onClick={() => setShowPublish(true)}
              className="mt-3 inline-flex min-h-[44px] items-center gap-1 rounded-xl border-2 border-brand-200 px-4 py-2 text-sm font-medium text-brand-600 transition-colors hover:bg-brand-50"
            >
              🌟 Share to Spotlight
            </button>
          </motion.div>

          {/* Tool cards grid */}
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TOOL_CARDS.map((card, index) => (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {card.active ? (
                  <button
                    onClick={() => navigate(`/hq/${planId}/${card.path}`)}
                    className="group flex w-full flex-col items-center rounded-2xl border-2 border-gray-100 bg-white p-6 text-center shadow-sm transition-all hover:scale-[1.03] hover:border-brand-200 hover:shadow-lg"
                  >
                    <span className="text-4xl transition-transform group-hover:scale-110">
                      {card.icon}
                    </span>
                    <h3 className="mt-3 text-lg font-bold text-gray-900">
                      {card.name}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {card.description}
                    </p>
                  </button>
                ) : (
                  <div className="flex w-full flex-col items-center rounded-2xl border-2 border-gray-100 bg-gray-50 p-6 text-center opacity-60">
                    <span className="text-4xl grayscale">{card.icon}</span>
                    <h3 className="mt-3 text-lg font-bold text-gray-400">
                      {card.name}
                    </h3>
                    <p className="mt-1 text-sm text-gray-400">
                      {card.description}
                    </p>
                    <span className="mt-2 inline-block rounded-full bg-gray-200 px-3 py-0.5 text-xs font-medium text-gray-500">
                      Coming Soon
                    </span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Back to launchpad link */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate(`/launchpad/${planId}`)}
              className="text-sm font-medium text-brand-500 hover:text-brand-600"
            >
              ← Back to Launchpad
            </button>
          </div>
        </div>
      </div>
      <PublishModal
        visible={showPublish}
        onClose={() => setShowPublish(false)}
        defaultContentType="business"
        defaultCaption={plan.elevatorPitchSolution || ''}
        planId={planId}
        businessName={plan.businessName}
      />
    </AnimatedPage>
  );
}
